/**
 * useUserLocale composable
 *
 * Le cookie `locale` (utils/localeCookie) démarre l'app sans latence, mais il
 * ne suit pas la langue du compte : phoenix la pilote depuis `me.locale_id`
 * sans jamais écrire ce cookie, donc un utilisateur qui change de langue là-bas
 * revenait ici dans l'ancienne. `me` fait donc foi dès qu'on est authentifié —
 * ce que faisait af/auth avec spaces/page.tsx → check-spaces, et ce que fait
 * personal-account dans son App.vue.
 *
 *  - syncLocaleFromUser() — garde du router : aligne l'app sur la langue du
 *                           compte avant le premier rendu (donc sans flash).
 *  - persistLocaleForUser() — switcher : écrit le choix sur le compte, sinon la
 *                           synchro ci-dessus l'annulerait au chargement suivant.
 */
import { useCookies } from '@/composables/useCookies';
import { changeLanguage, i18next, LOCALES } from '@/config/i18n';
import { updateUserLocale } from '@/services/auth';
import { fetchLocales } from '@/services/cdn';
import { useUserStore } from '@/stores/user';
import { persistLocaleCookie } from '@/utils/localeCookie';

/**
 * Une seule fois par chargement de page : au-delà, un changement manuel fait
 * dans le switcher serait annulé à la navigation suivante.
 */
let synced = false;

/** Locale supportée par welcome la plus proche, à défaut la même langue. */
function toSupportedLocale(iso: string | undefined): string | undefined {
  if (!iso) return undefined;

  return LOCALES.find(l => l === iso) ?? LOCALES.find(l => l.startsWith(`${iso.split('-')[0]}-`));
}

export function useUserLocale() {
  async function syncLocaleFromUser(): Promise<void> {
    if (synced) return;
    synced = true;

    try {
      // Le store dédoublonne : sur /spaces la vue demande déjà le même /me.
      const [user, locales] = await Promise.all([useUserStore().fetch(), fetchLocales()]);

      const iso = toSupportedLocale(locales.find(l => l.id === user.locale_id)?.iso_code_639);

      if (!iso) return;

      if (iso !== i18next.language) {
        await changeLanguage(iso);
        return;
      }

      // Déjà la bonne langue (cookie ou navigator) : on repose quand même le
      // cookie, sinon welcome n'émet rien pour les apps qui démarrent dessus —
      // et c'est aussi ce qui y grave l'`id` s'il était absent ou dégradé.
      await persistLocaleCookie(iso);
    } catch {
      // Session expirée ou CDN injoignable — la langue du cookie reste en place
    }
  }

  async function persistLocaleForUser(iso: string): Promise<void> {
    // Indicateur `af-auth`, comme le garde du router : sur une page publique il
    // n'y a pas de compte à mettre à jour, le cookie suffit.
    if (!useCookies().has('af-auth')) return;

    try {
      const locales = await fetchLocales();
      const localeId = locales.find(l => l.iso_code_639 === iso)?.id;

      if (localeId) await updateUserLocale(localeId);
    } catch {
      // Non bloquant : la langue reste appliquée localement via le cookie
    }
  }

  return { syncLocaleFromUser, persistLocaleForUser };
}
